import { adminRpcClient } from '@/providers/backendClient';
import { Box, Button } from '@mui/material';
import { useCallback, useState } from 'react';
import { useRecordContext } from 'react-admin';
import ReactJson from 'react-json-view';

export const AppTrackerCommandPanel = () => {
  const record = useRecordContext();

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      alignItems="flex-start"
    >
      <ReactJson
        src={record}
        theme="monokai"
        displayDataTypes={false}
        enableClipboard={true}
        collapsed={2}
        style={{
          backgroundColor: 'transparent',
          fontFamily: 'monospace',
        }}
      />
    </Box>
  );
};

export type DownloadFileButtonProps = {
  filePath: string;
};

export const DownloadFileButton = ({
  filePath,
}: DownloadFileButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = useCallback(async () => {
    setIsDownloading(true);

    const uri = await adminRpcClient(
      'PUBLIC_ADMIN_getDataExportsDownloadUri',
      [filePath],
    ).catch(error => {
      alert(`Failed to download file: ${error.message}`);

      return null;
    });

    setIsDownloading(false);

    if (!uri) {
      return;
    }

    const parsedFileName = (filePath || '').replace('/', '-');

    await startFileDownload(uri as string, parsedFileName);
  }, [filePath]);

  return (
    <Button onClick={downloadFile} disabled={isDownloading}>
      {isDownloading ? 'Downloading' : 'Download'} File
      {isDownloading ? '...' : ''}
    </Button>
  );
};

export const startFileDownload = async (
  uri: string,
  filename: string,
) => {
  // Navigate straight to the SAS URL instead of fetch()+blob. A browser fetch to
  // *.blob.core.windows.net is a cross-origin XHR and is blocked unless the storage
  // account has a CORS rule for this origin; a direct anchor navigation is not subject
  // to CORS. `download` is ignored cross-origin (filename comes from the blob), which is
  // fine — the SAS URL is already authorized to download the file.
  const anchor = document.createElement('a');
  anchor.href = uri;
  anchor.download = filename;
  anchor.target = '_blank';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};
